import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Upload, X, Image as ImageIcon, Loader2, GripVertical } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const MAX_DIMENSION = 1920;
const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

function resizeImage(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    // HEIC/HEIF는 브라우저에서 디코딩 불가 → 원본 그대로 서버로 전송 (서버에서 변환)
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.heic') || name.endsWith('.heif')) { resolve(file); return; }
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const needsResize = width > MAX_DIMENSION || height > MAX_DIMENSION;
      if (needsResize) {
        if (width > height) {
          height = Math.round(height * (MAX_DIMENSION / width));
          width = MAX_DIMENSION;
        } else {
          width = Math.round(width * (MAX_DIMENSION / height));
          height = MAX_DIMENSION;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const tryQuality = (q) => {
        canvas.toBlob((blob) => {
          if (blob.size > MAX_FILE_SIZE && q > 0.3) {
            tryQuality(q - 0.1);
          } else {
            const resized = new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() });
            resolve(resized);
          }
        }, 'image/jpeg', q);
      };

      if (!needsResize && file.size <= MAX_FILE_SIZE) {
        resolve(file);
      } else {
        tryQuality(0.85);
      }
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });
}
const CATEGORIES = [
  { value: 'FESTIVAL', label: '축제' },
  { value: 'EVENT', label: '공연' },
];

export default function PaperAdminWorkFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: '', tagline: '', category: 'EVENT', workDate: '', workDateEnd: '', content: '',
    location: '', clientName: '', youtubeUrl: '', sortOrder: 0,
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]); // existing from DB
  const [newGalleryFiles, setNewGalleryFiles] = useState([]);
  const [newGalleryCaptions, setNewGalleryCaptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [galleryDragOver, setGalleryDragOver] = useState(false);
  const [galleryProcessing, setGalleryProcessing] = useState(null); // { done, total }
  const [galleryOrderChanged, setGalleryOrderChanged] = useState(false);
  const thumbnailRef = useRef(null);
  const posterRef = useRef(null);
  const galleryRef = useRef(null);
  const dragItemRef = useRef(null);
  const dragOverRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/api/paperWork/${id}`)
      .then(res => {
        const w = res.data.work;
        setForm({
          title: w.title || '', tagline: w.tagline || '',
          category: w.category || 'EVENT',
          workDate: w.workDate || '', workDateEnd: w.workDateEnd || '', content: w.content || '',
          location: w.location || '', clientName: w.clientName || '',
          youtubeUrl: w.youtubeUrl || '', sortOrder: w.sortOrder || 0,
        });
        if (w.thumbnailUrl) setThumbnailPreview(w.thumbnailUrl);
        if (w.posterUrl) setPosterPreview(w.posterUrl);
        if (w.images) setGalleryImages(w.images.sort((a, b) => a.sortOrder - b.sortOrder));
      })
      .catch(() => alert('작품 정보를 불러올 수 없습니다.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'sortOrder' ? parseInt(value) || 0 : value }));
  };

  const isHeic = (f) => /\.(heic|heif)$/i.test(f.name || '');
  const HEIC_PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect fill="#1a1428" width="200" height="200"/><text x="100" y="95" text-anchor="middle" fill="#a78bfa" font-size="14" font-family="sans-serif">HEIC</text><text x="100" y="115" text-anchor="middle" fill="#666" font-size="11" font-family="sans-serif">서버에서 변환됩니다</text></svg>');

  const handleThumbnail = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setThumbnailFile(resized);
    setThumbnailPreview(isHeic(file) ? HEIC_PLACEHOLDER : URL.createObjectURL(resized));
  };

  const handlePoster = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const resized = await resizeImage(file);
    setPosterFile(resized);
    setPosterPreview(isHeic(file) ? HEIC_PLACEHOLDER : URL.createObjectURL(resized));
  };

  const addGalleryFiles = useCallback(async (files) => {
    const fileArr = Array.from(files).filter(f => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (fileArr.length === 0) return;
    const total = fileArr.length;
    setGalleryProcessing({ done: 0, total });
    for (let i = 0; i < fileArr.length; i++) {
      const resized = await resizeImage(fileArr[i]);
      setNewGalleryFiles(prev => [...prev, resized]);
      setNewGalleryCaptions(prev => [...prev, '']);
      setGalleryProcessing({ done: i + 1, total });
    }
    setGalleryProcessing(null);
  }, []);

  const handleGalleryFiles = (e) => {
    addGalleryFiles(e.target.files);
  };

  const handleGalleryDrop = useCallback((e) => {
    e.preventDefault();
    setGalleryDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) addGalleryFiles(files);
  }, [addGalleryFiles]);

  const handleGalleryDragOver = useCallback((e) => {
    e.preventDefault();
    setGalleryDragOver(true);
  }, []);

  const handleGalleryDragLeave = useCallback(() => {
    setGalleryDragOver(false);
  }, []);

  const removeNewGalleryFile = (index) => {
    setNewGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setNewGalleryCaptions(prev => prev.filter((_, i) => i !== index));
  };

  const deleteGalleryImage = async (imageId) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`${API}/api/paperWork/images/${imageId}`, { headers });
      setGalleryImages(prev => prev.filter(img => img.id !== imageId));
    } catch {
      alert('이미지 삭제 중 오류가 발생했습니다.');
    }
  };

  // 갤러리 통합 리스트 (기존 + 새 파일)
  const allGalleryItems = [
    ...galleryImages.map(img => ({ type: 'existing', img })),
    ...newGalleryFiles.map((file, i) => ({ type: 'new', file, caption: newGalleryCaptions[i] || '' })),
  ];

  const handleItemDragStart = (idx) => { dragItemRef.current = idx; };
  const handleItemDragEnter = (idx) => { dragOverRef.current = idx; };
  const handleItemDragEnd = () => {
    if (dragItemRef.current === null || dragOverRef.current === null) return;
    if (dragItemRef.current === dragOverRef.current) {
      dragItemRef.current = null;
      dragOverRef.current = null;
      return;
    }
    const items = [...allGalleryItems];
    const [dragged] = items.splice(dragItemRef.current, 1);
    items.splice(dragOverRef.current, 0, dragged);

    setGalleryImages(items.filter(i => i.type === 'existing').map(i => i.img));
    setNewGalleryFiles(items.filter(i => i.type === 'new').map(i => i.file));
    setNewGalleryCaptions(items.filter(i => i.type === 'new').map(i => i.caption));
    setGalleryOrderChanged(true);

    dragItemRef.current = null;
    dragOverRef.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert('제목을 입력해주세요.');

    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val !== null && val !== undefined) formData.append(key, val);
      });
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);
      if (posterFile) formData.append('poster', posterFile);

      let workId;
      if (isEdit) {
        await axios.put(`${API}/api/paperWork/${id}`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        workId = id;
      } else {
        const res = await axios.post(`${API}/api/paperWork`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        workId = res.data.id;
      }

      // 기존 이미지 순서 업데이트
      if (galleryOrderChanged && galleryImages.length > 0) {
        const imageOrders = galleryImages.map((img, i) => ({ id: img.id, sortOrder: i + 1 }));
        await axios.put(`${API}/api/paperWork/${workId}/images/order`, { imageOrders }, { headers });
      }

      // Upload new gallery images
      if (newGalleryFiles.length > 0) {
        const imgData = new FormData();
        newGalleryFiles.forEach(file => imgData.append('images', file));
        imgData.append('captions', JSON.stringify(newGalleryCaptions));
        // 기존 이미지 순서 뒤에 이어서 정렬
        imgData.append('startOrder', String(galleryImages.length + 1));
        await axios.post(`${API}/api/paperWork/${workId}/images`, imgData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate(`${PAPER_BASE}/admin/works`);
    } catch (err) {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate(`${PAPER_BASE}/admin/works`)} className="text-white/40 hover:text-white/60 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-light text-white tracking-wide">
          {isEdit ? '수정' : '추가'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Title */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">제목 *</label>
          <input
            type="text" name="title" value={form.title} onChange={handleChange}
            className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            placeholder="작품 제목"
          />
        </div>

        {/* Tagline */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">한줄평 (태그라인)</label>
          <input
            type="text" name="tagline" value={form.tagline} onChange={handleChange}
            className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            placeholder="카드 이미지 위에 표시되는 한줄평"
          />
        </div>

        {/* Category & Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">카테고리</label>
            <select
              name="category" value={form.category} onChange={handleChange}
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            >
              {CATEGORIES.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">시작일</label>
            <input
              type="date" name="workDate" value={form.workDate} onChange={handleChange}
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">종료일</label>
            <input
              type="date" name="workDateEnd" value={form.workDateEnd} onChange={handleChange}
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            />
            <p className="text-white/20 text-xs mt-1">비워두면 시작일만 표시됩니다.</p>
          </div>
        </div>

        {/* Location & Client */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">장소</label>
            <input
              type="text" name="location" value={form.location} onChange={handleChange}
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
              placeholder="장소"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">의뢰사</label>
            <input
              type="text" name="clientName" value={form.clientName} onChange={handleChange}
              className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
              placeholder="의뢰사"
            />
          </div>
        </div>

        {/* Content */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">내용</label>
          <textarea
            name="content" value={form.content} onChange={handleChange} rows={6}
            className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors resize-none"
            placeholder="작품에 대한 설명"
          />
        </div>

        {/* YouTube URL */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">유튜브 영상 URL</label>
          <input
            type="text" name="youtubeUrl" value={form.youtubeUrl} onChange={handleChange}
            className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <p className="text-white/20 text-xs mt-1">상세페이지 갤러리 위에 영상이 노출됩니다.</p>
        </div>

        {/* Sort Order */}
        <div className="max-w-xs">
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">정렬 순서</label>
          <input
            type="number" name="sortOrder" value={form.sortOrder} onChange={handleChange}
            className="w-full bg-[#1a1428] border border-white/10 rounded-lg px-4 py-3 text-white text-sm outline-none focus:border-violet-400/50 transition-colors"
          />
          <p className="text-white/20 text-xs mt-1">숫자가 높을수록 먼저 표시됩니다.</p>
        </div>

        {/* Thumbnail */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">대표 이미지</label>
          <input type="file" ref={thumbnailRef} accept="image/*,.heic,.heif" onChange={handleThumbnail} className="hidden" />
          {thumbnailPreview ? (
            <div className="relative inline-block">
              <img src={thumbnailPreview} alt="thumbnail" className="h-40 rounded-lg object-cover" />
              <button
                type="button"
                onClick={() => { setThumbnailFile(null); setThumbnailPreview(null); thumbnailRef.current.value = ''; }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => thumbnailRef.current.click()}
              className="flex items-center gap-3 px-6 py-4 border border-dashed border-white/10 rounded-lg text-white/30 hover:border-violet-400/30 hover:text-violet-400/60 transition-all"
            >
              <Upload size={18} /> 이미지 업로드
            </button>
          )}
        </div>

        {/* Poster */}
        <div>
          <label className="block text-xs tracking-[0.15em] text-white/40 mb-2 uppercase">포스터 이미지</label>
          <p className="text-white/20 text-xs mb-3">상세페이지에 표시되는 포스터 이미지 (세로형 권장, 2:3 비율)</p>
          <input type="file" ref={posterRef} accept="image/*,.heic,.heif" onChange={handlePoster} className="hidden" />
          {posterPreview ? (
            <div className="relative inline-block">
              <img src={posterPreview} alt="poster" className="h-52 rounded-lg object-cover" />
              <button
                type="button"
                onClick={() => { setPosterFile(null); setPosterPreview(null); posterRef.current.value = ''; }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => posterRef.current.click()}
              className="flex items-center gap-3 px-6 py-4 border border-dashed border-white/10 rounded-lg text-white/30 hover:border-violet-400/30 hover:text-violet-400/60 transition-all"
            >
              <Upload size={18} /> 포스터 업로드
            </button>
          )}
        </div>

        {/* Gallery Images */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-xs tracking-[0.15em] text-white/40 uppercase">
              갤러리 이미지 {allGalleryItems.length > 0 && `(${allGalleryItems.length})`}
            </label>
            {allGalleryItems.length > 1 && (
              <span className="text-white/20 text-[10px]">드래그하여 순서를 변경할 수 있습니다</span>
            )}
          </div>
          <input type="file" ref={galleryRef} accept="image/*,.heic,.heif" multiple onChange={handleGalleryFiles} className="hidden" />

          <div
            onDrop={handleGalleryDrop}
            onDragOver={handleGalleryDragOver}
            onDragLeave={handleGalleryDragLeave}
            className={`grid grid-cols-3 md:grid-cols-4 gap-3 mb-4 p-3 rounded-xl border-2 border-dashed transition-all ${
              galleryDragOver ? 'border-violet-400/50 bg-violet-400/5' : 'border-transparent'
            }`}>
            {allGalleryItems.map((item, idx) => (
              <div
                key={item.type === 'existing' ? `e-${item.img.id}` : `n-${idx}`}
                draggable={!galleryProcessing}
                onDragStart={() => handleItemDragStart(idx)}
                onDragEnter={() => handleItemDragEnter(idx)}
                onDragEnd={handleItemDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="cursor-grab active:cursor-grabbing group/item"
              >
                <div className="relative aspect-square rounded-lg overflow-hidden bg-[#1a1428]">
                  <img
                    src={item.type === 'existing' ? item.img.imageUrl : (isHeic(item.file) ? HEIC_PLACEHOLDER : URL.createObjectURL(item.file))}
                    alt=""
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                  {/* 드래그 핸들 */}
                  <div className="absolute top-1 left-1 p-1 text-white/30 opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <GripVertical size={14} />
                  </div>
                  {/* 순서 번호 */}
                  <span className="absolute bottom-1 left-1 bg-black/60 text-white/60 text-[9px] px-1.5 py-0.5 rounded font-medium">
                    {idx + 1}
                  </span>
                  {/* 삭제 버튼 */}
                  <button
                    type="button"
                    onClick={() => item.type === 'existing' ? deleteGalleryImage(item.img.id) : removeNewGalleryFile(newGalleryFiles.indexOf(item.file))}
                    className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors opacity-0 group-hover/item:opacity-100"
                  >
                    <X size={12} />
                  </button>
                  {/* 새 이미지 뱃지 */}
                  {item.type === 'new' && (
                    <span className="absolute top-1 left-1/2 -translate-x-1/2 bg-violet-500/80 text-white text-[8px] px-1.5 py-0.5 rounded-full font-medium">NEW</span>
                  )}
                </div>
                {item.type === 'existing' ? (
                  <input
                    type="text"
                    placeholder="캡션"
                    defaultValue={item.img.caption || ''}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      if (val !== (item.img.caption || '')) {
                        axios.put(`${API}/api/paperWork/images/${item.img.id}/caption`, { caption: val }, { headers })
                          .then(() => setGalleryImages(prev => prev.map(g => g.id === item.img.id ? { ...g, caption: val } : g)))
                          .catch(() => {});
                      }
                    }}
                    className="w-full mt-1 px-2 py-1 bg-[#1a1428] border border-white/10 rounded text-white/60 text-[11px] outline-none focus:border-violet-400/50"
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="캡션"
                    value={item.caption}
                    onChange={(e) => {
                      const val = e.target.value;
                      const fileIdx = newGalleryFiles.indexOf(item.file);
                      if (fileIdx >= 0) setNewGalleryCaptions(prev => prev.map((c, i) => i === fileIdx ? val : c));
                    }}
                    className="w-full mt-1 px-2 py-1 bg-[#1a1428] border border-white/10 rounded text-white/60 text-[11px] outline-none focus:border-violet-400/50"
                  />
                )}
              </div>
            ))}

            {/* Add button */}
            {!galleryProcessing && (
              <button
                type="button"
                onClick={() => galleryRef.current.click()}
                className="aspect-square rounded-lg border border-dashed border-white/10 flex flex-col items-center justify-center text-white/20 hover:border-violet-400/30 hover:text-violet-400/40 transition-all"
              >
                <ImageIcon size={20} />
                <span className="text-[10px] mt-1">추가</span>
              </button>
            )}
          </div>

          {/* 이미지 처리 진행률 */}
          {galleryProcessing && (
            <div className="mb-3 rounded-lg bg-violet-500/10 border border-violet-400/20 p-4">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 size={16} className="text-violet-400 animate-spin" />
                <span className="text-violet-300 text-sm font-medium">
                  이미지 처리 중... {galleryProcessing.done} / {galleryProcessing.total}
                </span>
              </div>
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 rounded-full transition-all duration-300"
                  style={{ width: `${(galleryProcessing.done / galleryProcessing.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          <p className="text-white/20 text-xs">이미지를 드래그하여 추가할 수 있습니다. 이미지는 자동으로 1920px / 1MB 이하로 리사이징됩니다.</p>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={saving || !!galleryProcessing}
            className="flex items-center gap-2 px-8 py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <Save size={16} />
            {saving ? '저장 중...' : (isEdit ? '수정' : '등록')}
          </button>
          <button
            type="button"
            onClick={() => navigate(`${PAPER_BASE}/admin/works`)}
            className="px-8 py-3 border border-white/10 text-white/50 text-sm rounded-lg hover:border-white/20 transition-colors"
          >
            취소
          </button>
        </div>
      </form>
    </div>
  );
}
